<script setup lang="ts">

import { ref, computed } from 'vue';
import { useUserDataStore } from '@/user/store/userDataStore';
import type { BankAccount } from '@/api/bankAccount/types';
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElTag } from 'element-plus';
import { NotificationVariant, useNotification } from '@/application/components/ui/useNotification';
import ShallowCardCollapsible from '@/application/components/ShallowCardCollapsible.vue';
import { useDetectMobile } from '@/application/composables/detectMobile';
import ConfirmModal from '@/application/components/ConfirmModal.vue';
import EditBankAccountModal from '@/user/components/EditBankAccountModal.vue';
import DetailViewContainer from '@/contracts/components/DetailViewContainer.vue';
import { useContractStore } from '@/contracts/stores/contractStore';
import { useRouter } from 'vue-router';
import { Finanzmanager } from '@/application/router/types/types';
import { spartenMapper } from '@/application/utils/sparteFormatter';
import { TrackEventCategory } from '@/application/utils/tracking/events';
import { storeToRefs } from 'pinia';
import type { Contract } from "@/api/contracts/types";
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

interface Props {
  bankAccount: BankAccount;
}

const props = defineProps<Props>();

const router = useRouter();
const { show } = useNotification();
const { isMobile } = useDetectMobile();
const { contractsMap } = storeToRefs(useContractStore());
const { deleteBankAccountById } = useUserDataStore();
const { duzen } = useBaseDataStore();

const menuOpen = ref<boolean>(false);
const showEditModal = ref<boolean>(false);
const showDeleteConfirm = ref<boolean>(false);

const bankAccountContracts = computed(() => {
  return props.bankAccount.contractIds.reduce((acc, contractId) => {
    const contract = contractsMap.value.get(contractId);
    if (contract) {
      acc.push(contract);
    }
    return acc;
  }, [] as Contract[]);
})

const doAction = (item) => {
  Handler[item.action]()
}

const Handler = {
  edit: () => showEditModal.value = true,
  delete: () => showDeleteConfirm.value = true,
};

async function submitDelete() {
  const res = await deleteBankAccountById(props.bankAccount.id)

  if (res) {
    useTagManager().push({ event: TrackEventCategory.BANK_ACCOUNT, action: 'entfernen' })
    await router.push({ name: Finanzmanager.BANK_ACCOUNTS });
    show('Bankverbindung erfolgreich entfernt.', NotificationVariant.Success);
  } else {
    show('Es gab einen Fehler.', NotificationVariant.Error);
  }
}

const items = [
  { text: 'Bearbeiten', action: 'edit', icon: 'pencil', class: 'text-gray-900' },
  { text: 'Löschen', action: 'delete', icon: 'trash', class: 'text-red-700' },
];

const bankAccountDetails = computed(() => [
  { label: 'Bezeichnung', value: props.bankAccount.note.length > 0 ? props.bankAccount.note : '-' },
  { label: 'IBAN', value: props.bankAccount.iban },
  { label: 'BIC', value: props.bankAccount.bic },
  { label: 'Kontoinhaber', value: props.bankAccount.depositor },
]);
</script>

<template>
  <DetailViewContainer>
    <template #title>
      <div class="flex flex-row items-center justify-between">
        <h3 class="text-gray-900">{{ bankAccount.bankName }}</h3>
        <ElDropdown
          trigger="click"
          placement="bottom-end"
          class="ml-auto"
          @visible-change="menuOpen = $event"
          @command="doAction"
        >
          <button
            type="button"
            aria-label="Menü"
            class="text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-lg"
            :class="{'bg-gray-100': menuOpen}"
          >
            <FaIcon name="ellipsis" variant="regular" size="lg" />
          </button>

          <template #dropdown>
            <ElDropdownMenu class="el-dropdown-menu--plain w-[260px] py-2">
              <ElDropdownItem
                v-for="item in items"
                :key="item.action"
                :command="item"
              >
                <div
                  class="flex flex-row items-center gap-4 py-2 px-5 w-full"
                  :class="[item.class]"
                >
                  <FaIcon
                    v-if="item.icon"
                    :name="item.icon"
                    variant="light"
                  />
                  {{ item.text }}
                </div>
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </template>

    <template #main>
      <div class="flex flex-col">
        <ElTag
          v-if="bankAccount.main"
          type="primary"
          class="mr-auto"
          round
        >
          Standard-Bankverbindung
        </ElTag>
        <div class="flex flex-col items-start gap-3 mt-8">
          <div
            v-for="item in bankAccountDetails"
            :key="item.label"
            class="flex flex-col-reverse sm:flex-row justify-between md:items-end w-full"
          >
            <span class="font-normal text-gray-700/85 text-sm">{{ item.label }}</span>
            <div class="flex items-center gap-2">
              <span
                v-if="item.label === 'IBAN'"
                class="font-normal text-gray-700/85 text-sm"
              >endet auf</span>
              <span class="font-medium text-base text-gray-900">{{ item.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #extra>
      <ShallowCardCollapsible
        :title="`Verträge (${bankAccount.contractIds.length})`"
        :disabled="bankAccount.contractIds.length === 0"
        :open="bankAccount.contractIds.length > 0 && !isMobile"
        :max-height="'27rem'"
        no-hover
      >
        <RouterLink
          v-for="contract in bankAccountContracts"
          :key="contract.id"
          v-slot="{ navigate }"
          :to="{ name: Finanzmanager.VERTRAG, params: { id: contract.id} }"
          custom
        >
          <div
            class="flex flex-row items-center justify-between px-3 py-2.5 hover:bg-gray-100 rounded-sm group"
            @click="navigate"
          >
            <div class="flex flex-col">
              <span class="font-medium text-gray-900">
                {{ spartenMapper(contract.product) }} - {{ contract.number }}
              </span>
              <span class="text-sm text-gray-700">{{ contract.company.name }}</span>
            </div>
            <FaIcon
              name="chevron-right"
              variant="light"
              size="sm"
              class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-900"
            />
          </div>
        </RouterLink>
      </ShallowCardCollapsible>
    </template>
  </DetailViewContainer>
  <EditBankAccountModal
    v-model:show="showEditModal"
    :bank-account="bankAccount"
  />

  <ConfirmModal
    v-model:show="showDeleteConfirm"
    title="Bankverbindung löschen"
    variant="danger"
    icon="trash"
    :confirm="submitDelete"
  >
    Das Löschen der Bankverbindung kann nicht rückgängig gemacht werden.
    {{ duzen('Bist du sicher, dass du fortfahren möchtest?', 'Sind Sie sicher, dass Sie fortfahren möchten?') }}
  </ConfirmModal>
</template>
